import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { db, doc, updateDoc, serverTimestamp } from '../../firebase/config';
import SettingsLayout from './SettingsLayout';
import { Check, Globe } from 'lucide-react';

const LANGUAGES = [
  { id: 'en-ZA', label: 'English (South Africa)', flag: '🇿🇦' },
  { id: 'en-US', label: 'English (US)', flag: '🇺🇸' },
  { id: 'en-GB', label: 'English (UK)', flag: '🇬🇧' },
  { id: 'af', label: 'Afrikaans', flag: '🇿🇦' },
  { id: 'zu', label: 'isiZulu', flag: '🇿🇦' },
  { id: 'xh', label: 'isiXhosa', flag: '🇿🇦' },
];

const LanguagePage = () => {
  const { user, userData } = useAuth();
  const [selected, setSelected] = useState(
    userData?.language || userData?.locale || 'en-ZA'
  );
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2200);
  };

  const handleSelect = async (langId) => {
    if (langId === selected) return;
    setSelected(langId);
    setSaving(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        language: langId,
        locale: langId,
        updatedAt: serverTimestamp(),
      });
      showToast('Language updated');
    } catch (err) {
      console.error('Save language failed:', err);
      showToast('Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsLayout
      title="Language"
      subtitle="Choose your preferred language"
    >
      <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden">
        {LANGUAGES.map((lang, index) => {
          const isSelected = selected === lang.id;
          return (
            <button
              key={lang.id}
              onClick={() => handleSelect(lang.id)}
              disabled={saving}
              className={`w-full p-4 flex items-center gap-4 text-left transition-colors hover:bg-[#FCF8F9] ${
                index < LANGUAGES.length - 1 ? 'border-b border-gray-50' : ''
              }`}
            >
              <div className="w-10 h-10 rounded-full bg-[#FCF8F9] flex items-center justify-center text-lg shrink-0">
                {lang.flag}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm ${isSelected ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`}>
                  {lang.label}
                </p>
              </div>
              {isSelected && (
                <div className="w-6 h-6 rounded-full bg-[#EFA9B6] flex items-center justify-center shrink-0">
                  <Check size={14} className="text-white" strokeWidth={3} />
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex items-start gap-2 p-4 bg-[#F4EEFF] rounded-2xl">
        <Globe size={16} className="text-[#AF8BD4] shrink-0 mt-0.5" />
        <p className="text-xs text-gray-600 leading-relaxed">
          Your language preference is saved to your account. Full translations are rolling out soon — for now the app displays in English.
        </p>
      </div>

      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[70]">
          <div className={`px-5 py-3 rounded-full shadow-lg text-sm font-bold ${
            toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-[#010101] text-white'
          }`}>
            {toast.msg}
          </div>
        </div>
      )}
    </SettingsLayout>
  );
};

export default LanguagePage;